import DOMPurify from "dompurify";

/**
 * Rich-HTML sanitization for advisor-facing surfaces that render AI-generated
 * or externally-sourced HTML via `dangerouslySetInnerHTML`
 * (email campaign bodies, AI email drafts, article-import drafts, generated
 * analysis reports, termination letters).
 *
 * Threat: HTML produced from AI output / imported articles can carry
 * `<script>`, event handlers (`onerror=`), `javascript:` URLs, or framed
 * content. Rendered inside the authenticated app origin this is stored/reflected
 * XSS in the advisor's session (same-origin API calls with their cookies).
 *
 * Primary control = DOMPurify in the browser (these are client components, so
 * sanitization runs on hydration). The server-side `stripDangerousHtml` is a
 * conservative defense-in-depth fallback for the SSR initial paint; the browser
 * re-sanitizes the same string after mount.
 *
 * Formatting (inline styles, links, images, lists, tables) is preserved so the
 * preview still looks correct — only dangerous constructs are removed.
 */

const FORBID_TAGS = [
  "script",
  "iframe",
  "object",
  "embed",
  "base",
  "form",
  "noscript",
];

function decodeAttributeForSafetyCheck(value: string): string {
  return value
    .replace(/&#x([0-9a-f]+);?/gi, (_match, hex: string) =>
      String.fromCodePoint(Number.parseInt(hex, 16)),
    )
    .replace(/&#([0-9]+);?/g, (_match, decimal: string) =>
      String.fromCodePoint(Number.parseInt(decimal, 10)),
    )
    .replace(/&colon;?/gi, ":")
    .replace(/[\u0000-\u0020\u007F-\u009F]/g, "")
    .toLowerCase();
}

function isDangerousUrlAttribute(value: string): boolean {
  return /^(?:javascript|vbscript|data:text\/html)/i.test(
    decodeAttributeForSafetyCheck(value),
  );
}

function isDangerousInlineStyle(value: string): boolean {
  const normalized = decodeAttributeForSafetyCheck(value);
  return /(?:expression\(|url\(["']?(?:javascript|vbscript|data:text\/html)|-moz-binding|behavior:)/i.test(
    normalized,
  );
}

/**
 * Conservative HTML strip used on the server (SSR) and as a hard fallback.
 * Not a substitute for DOMPurify, but guarantees no live script / handler /
 * dangerous URL ships in the initial HTML payload.
 */
export function stripDangerousHtml(html: string): string {
  return html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, "")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style\s*>/gi, "")
    .replace(/<\/?(?:iframe|object|embed|base|form|noscript|meta|link)\b[^>]*>/gi, "")
    // strip inline event handlers: on*="..." | on*='...' | on*=value
    .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, "")
    .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, "")
    // srcdoc is executable nested markup even without an iframe element.
    .replace(/\ssrcdoc\s*=\s*(["'])[\s\S]*?\1/gi, "")
    .replace(/\ssrcdoc\s*=\s*[^\s>]+/gi, "")
    // Neutralize dangerous URL schemes, including unquoted/entity-obfuscated forms.
    .replace(
      /\b(href|src|xlink:href)\s*=\s*(["'])([\s\S]*?)\2/gi,
      (match, attribute: string, _quote: string, value: string) =>
        isDangerousUrlAttribute(value) ? `${attribute}="#"` : match,
    )
    .replace(
      /\b(href|src|xlink:href)\s*=\s*([^\s"'=<>`]+)/gi,
      (match, attribute: string, value: string) =>
        isDangerousUrlAttribute(value) ? `${attribute}="#"` : match,
    )
    // Keep normal email inline formatting but remove executable CSS constructs.
    .replace(
      /\sstyle\s*=\s*(["'])([\s\S]*?)\1/gi,
      (match, _quote: string, value: string) =>
        isDangerousInlineStyle(value) ? "" : match,
    )
    .replace(
      /\sstyle\s*=\s*([^\s"'=<>`]+)/gi,
      (match, value: string) => (isDangerousInlineStyle(value) ? "" : match),
    );
}

/**
 * Sanitize untrusted rich HTML for safe rendering via
 * `dangerouslySetInnerHTML`. Returns "" for nullish input.
 */
export function sanitizeRichHtml(dirty: string | null | undefined): string {
  if (!dirty) return "";
  if (typeof window === "undefined" || typeof (DOMPurify as { sanitize?: unknown }).sanitize !== "function") {
    return stripDangerousHtml(dirty);
  }
  return DOMPurify.sanitize(dirty, {
    FORBID_TAGS,
    FORBID_ATTR: ["srcdoc"],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ["target"],
  });
}

/**
 * CSP for stored HTML opened inline (document preview in new tab).
 * Blocks scripts and nested frames while allowing styled static content.
 */
export const INLINE_HTML_PREVIEW_CSP =
  "default-src 'none'; style-src 'unsafe-inline'; img-src data: https: blob:; font-src data:; base-uri 'none'; form-action 'none'; frame-ancestors 'none';";

/** Sanitize pre-built report HTML blocks before print/PDF export. */
export function sanitizeReportHtmlBlocks(blocks: string[]): string {
  return blocks.map((block) => stripDangerousHtml(block)).join("\n");
}
